import {
  ContentClient,
  FallbackContentClient,
  StaticContentClient,
  StrapiContentClient,
} from '@accenturecodefoundry/mfe-pot-shared-content-client';

export const INTRO_CONTENT_KEY = 'employment-insurance.intro';

export const APPLICATIONS_CONTENT_KEYS = [
  'employment-insurance.applications.heading',
  'employment-insurance.applications.intro',
  'employment-insurance.applications.button',
  'employment-insurance.applications.confirmationDescription',
  'employment-insurance.applications.error',
  // Reused from claims -- "Claim {id}" is the same card title in both.
  'employment-insurance.claims.cardTitle',
] as const;

/**
 * The EI application wizard's labels -- condensed from the real
 * Service Canada online application's screens (see
 * `EiApplicationInput`/`EiApplicationForm.tsx` for the full mapping).
 * `otherOption`/`yesOption`/`noOption` are shared across multiple steps
 * (reason-for-separation's "Other", education level's "Other", and every
 * Yes/No picker in the wizard) rather than one key per occurrence.
 */
export const APPLICATION_FORM_CONTENT_KEYS = [
  'employment-insurance.application.breadcrumbHome',
  'employment-insurance.application.breadcrumbEi',
  'employment-insurance.application.breadcrumbCurrent',
  'employment-insurance.application.backButton',
  'employment-insurance.application.nextButton',
  'employment-insurance.application.submitButton',
  'employment-insurance.application.requiredError',
  'employment-insurance.application.yesOption',
  'employment-insurance.application.noOption',
  'employment-insurance.application.otherOption',
  // The four step titles of spec 014's consolidated flow.
  'employment-insurance.application.step1Title',
  'employment-insurance.application.step2Title',
  'employment-insurance.application.step3Title',
  'employment-insurance.application.step4Title',
  'employment-insurance.application.firstNameLabel',
  'employment-insurance.application.lastNameLabel',
  'employment-insurance.application.dateOfBirthLabel',
  'employment-insurance.application.addressLabel',
  'employment-insurance.application.addressLine1Label',
  'employment-insurance.application.addressLine2Label',
  'employment-insurance.application.cityLabel',
  'employment-insurance.application.provinceLabel',
  'employment-insurance.application.postalCodeLabel',
  'employment-insurance.application.phoneLabel',
  'employment-insurance.application.preferredLanguageLabel',
  'employment-insurance.application.languageEnglishOption',
  'employment-insurance.application.languageFrenchOption',
  'employment-insurance.application.employerNameLabel',
  'employment-insurance.application.lastDayWorkedLabel',
  'employment-insurance.application.jobTitleLabel',
  'employment-insurance.application.reasonLabel',
  'employment-insurance.application.reasonShortageOption',
  'employment-insurance.application.reasonDismissalOption',
  'employment-insurance.application.reasonQuitOption',
  'employment-insurance.application.payRateLabel',
  'employment-insurance.application.payPeriodLabel',
  'employment-insurance.application.payPeriodHourlyOption',
  'employment-insurance.application.payPeriodWeeklyOption',
  'employment-insurance.application.payPeriodBiweeklyOption',
  'employment-insurance.application.payPeriodMonthlyOption',
  'employment-insurance.application.hadOtherEmployersLabel',
  'employment-insurance.application.otherEmployerNameLabel',
  'employment-insurance.application.workersCompensationLabel',
  'employment-insurance.application.pensionLabel',
  'employment-insurance.application.selfEmployedLabel',
  'employment-insurance.application.trainingProgramLabel',
  // Spec 013's "other situations" checklist (step 5). The four labels
  // above are reused as checkbox labels.
  'employment-insurance.application.otherSituationsIntro',
  'employment-insurance.application.otherSituationsHeading',
  'employment-insurance.application.otherSituationsHint',
  'employment-insurance.application.unionMemberLabel',
  'employment-insurance.application.quebecParentalInsuranceLabel',
  'employment-insurance.application.relatedToEmployerLabel',
  'employment-insurance.application.unableToWorkMedicalLabel',
  'employment-insurance.application.incarceratedLabel',
  'employment-insurance.application.pregnantOrRecentBirthLabel',
  'employment-insurance.application.availableImmediatelyLabel',
  'employment-insurance.application.availableFromDateLabel',
  'employment-insurance.application.educationLevelLabel',
  'employment-insurance.application.educationHighSchoolOption',
  'employment-insurance.application.educationCollegeTradeOption',
  'employment-insurance.application.educationUndergraduateOption',
  'employment-insurance.application.educationGraduateOption',
  'employment-insurance.application.institutionNumberLabel',
  'employment-insurance.application.transitNumberLabel',
  'employment-insurance.application.accountNumberLabel',
  // Spec 014: step 3 payment additions and step 4 review additions.
  'employment-insurance.application.paymentMethodLabel',
  'employment-insurance.application.paymentDirectDepositOption',
  'employment-insurance.application.paymentChequeOption',
  'employment-insurance.application.paymentChequeNote',
  'employment-insurance.application.taxSlipLabel',
  'employment-insurance.application.taxSlipHint',
  'employment-insurance.application.taxSlipOnlineOption',
  'employment-insurance.application.taxSlipMailOption',
  'employment-insurance.application.taxDeductionLabel',
  'employment-insurance.application.taxDeductionHint',
  'employment-insurance.application.taxDeductionBasicOption',
  'employment-insurance.application.taxDeductionBasicAndSpouseOption',
  'employment-insurance.application.reviewBody',
  'employment-insurance.application.reviewFullApplicationLink',
  'employment-insurance.application.estimateNote',
  'employment-insurance.application.estimateBand',
  'employment-insurance.application.estimateTitle',
  'employment-insurance.application.estimatePerWeekLabel',
  'employment-insurance.application.declarationHeading',
  'employment-insurance.application.declarationIntro',
  'employment-insurance.application.declarationStatement1',
  'employment-insurance.application.declarationStatement2',
  'employment-insurance.application.declarationStatement3',
  'employment-insurance.application.declarationStatement4',
  'employment-insurance.application.statementsConfirmedLabel',
  'employment-insurance.application.consentToShareLabel',
  'employment-insurance.application.rightsAcceptedLabel',
  'employment-insurance.application.consentsRequiredError',
  'employment-insurance.application.thirdPartyHeading',
  'employment-insurance.application.thirdPartyLabel',
  'employment-insurance.application.helperNameLabel',
  'employment-insurance.application.reviewHeading',
  'employment-insurance.application.profileSaveError',
  // Spec 012's pre-fill confirm step (new step 1).
  'employment-insurance.application.preFillIntro',
  'employment-insurance.application.preFillAboutYouHeading',
  'employment-insurance.application.preFillLastJobHeading',
  'employment-insurance.application.preFillFullNameLabel',
  'employment-insurance.application.preFillInsurableHoursLabel',
  'employment-insurance.application.preFillInsurableEarningsLabel',
  'employment-insurance.application.preFillHoursUnit',
  'employment-insurance.application.preFillProfileNoticeTitle',
  'employment-insurance.application.preFillProfileNoticeBody',
  'employment-insurance.application.preFillEmploymentNoticeTitle',
  'employment-insurance.application.preFillEmploymentNoticeBody',
  'employment-insurance.application.confirmAndContinueButton',
  'employment-insurance.application.continueToPaymentButton',
] as const;

/** Every CMS key the application wizard (form + confirm step) can request. */
export type ApplicationFormContentKey = (typeof APPLICATION_FORM_CONTENT_KEYS)[number];

export const REPORTING_STATUS_CONTENT_KEYS = [
  'employment-insurance.reporting-status.heading',
  'employment-insurance.reporting-status.unavailable',
  'employment-insurance.reporting-status.noClaim',
  'employment-insurance.reporting-status.notYetDue',
  'employment-insurance.reporting-status.dueSoon',
  'employment-insurance.reporting-status.overdue',
  'employment-insurance.reporting-status.nextReportDue',
] as const;

/**
 * No CMS configured -> the bilingual fallback (`public/assets/content-fallback/<locale>.json`)
 * directly. CMS configured -> Strapi as primary, same fallback backing it up
 * if Strapi is unreachable/missing a key at runtime -- see `FallbackContentClient`.
 */
export function createContentClient(strapiBaseUrl: string | undefined, assetBaseUrl: string): ContentClient {
  const fallback = new StaticContentClient(assetBaseUrl);
  return strapiBaseUrl ? new FallbackContentClient(new StrapiContentClient(strapiBaseUrl), fallback) : fallback;
}

/**
 * Report mode's EI Reporting screen (docs/specs/016-ei-reporting-flow.md):
 * heading, report banner, Your reports table, Common questions, help card,
 * and the demo end-claim control. The six FAQ
 * entries carry the question in `title` and the answer in `body`.
 */
export const REPORTING_OVERVIEW_CONTENT_KEYS = [
  'employment-insurance.reporting-overview.title',
  'employment-insurance.reporting-overview.subtitle',
  'employment-insurance.reporting-overview.bannerTitle',
  'employment-insurance.reporting-overview.bannerBody',
  'employment-insurance.reporting-overview.reviewButton',
  'employment-insurance.reporting-overview.reportsHeading',
  'employment-insurance.reporting-overview.columnPeriod',
  'employment-insurance.reporting-overview.columnStatus',
  'employment-insurance.reporting-overview.columnPayment',
  'employment-insurance.reporting-overview.statusSubmitted',
  'employment-insurance.reporting-overview.statusDue',
  'employment-insurance.reporting-overview.statusOverdue',
  'employment-insurance.reporting-overview.statusUpcoming',
  'employment-insurance.reporting-overview.paymentToBePaid',
  'employment-insurance.reporting-overview.paymentPending',
  'employment-insurance.reporting-overview.faqHeading',
  'employment-insurance.reporting-overview.faq1',
  'employment-insurance.reporting-overview.faq2',
  'employment-insurance.reporting-overview.faq3',
  'employment-insurance.reporting-overview.faq4',
  'employment-insurance.reporting-overview.faq5',
  'employment-insurance.reporting-overview.faq6',
  'employment-insurance.reporting-overview.helpHeading',
  'employment-insurance.reporting-overview.helpTitle',
  'employment-insurance.reporting-overview.helpBody',
  'employment-insurance.reporting-overview.demoHeading',
  'employment-insurance.reporting-overview.demoEndClaimButton',
  'employment-insurance.reporting-overview.loadError',
  'employment-insurance.reporting-overview.endClaimError',
] as const;

export type ReportingOverviewContentKey = (typeof REPORTING_OVERVIEW_CONTENT_KEYS)[number];
